import { t } from "@hakili/i18n";
import { Maximize2 } from "lucide-react";
import type { ReactElement } from "react";

import { fill } from "./dossiers.js";

/**
 * `U-4`, `M2` (WF-BNK-18 « Interroger Hakili sur un dossier »): **the side panel with the file's
 * thread.**
 *
 * The drawing's own shape: the thread, three suggestions, a free field. What this build adds
 * beneath every answer is the thing the workflow's acceptance criterion asks for , **the doors the
 * answer rests on, named** , and the sentence that it decides nothing. A failed exchange is kept
 * in the thread with « en échec » in words (A47): a question that vanished would be a question
 * nobody could account for.
 */
export interface QuestionDoorView {
  readonly tool: string;
  readonly door: string;
  readonly status: number;
}

export interface QuestionExchangeView {
  /** `QUESTION-SCOPE`: the exchange's place in this file's thread (1 for the oldest) , the screen's opaque key, never printed. */
  readonly position: number;
  readonly question: string;
  readonly answer: string | null;
  readonly failure: string | null;
  /**
   * `ASK-WIDE` contract part 2 (lane AD): how the exchange ended, as the door serves it (`openapi.document.ts:424-452`): `ok`, `unverified` (the
   * figure guard), `refused`, `failed`. `null` where the door is older and sends none.
   */
  readonly state: "ok" | "unverified" | "refused" | "failed" | null;
  readonly doorsRead: readonly QuestionDoorView[];
  readonly askedAt: string;
  readonly answeredAt: string | null;
}

const STATES = ["ok", "unverified", "refused", "failed"] as const;

const asRecord = (value: unknown): Record<string, unknown> | null =>
  typeof value === "object" && value !== null ? (value as Record<string, unknown>) : null;

/** A word the door answered, or nothing , never an object coerced into `[object Object]`. */
const texte = (value: unknown): string => (typeof value === "string" ? value : "");

/**
 * `U-4`, `M2`: the thread as the door answers it, oldest first , and NOTHING invented where the
 * door said nothing. An exchange that failed keeps its question and its failure: the panel says
 * « en échec » rather than dropping the row, so no question is left unaccounted for (A47).
 */
export function threadFrom(raw: unknown): readonly QuestionExchangeView[] {
  const body = asRecord(raw);
  const rows = body === null ? null : body["thread"];
  if (!Array.isArray(rows)) return [];
  return rows.flatMap((one, at) => {
    const row = asRecord(one);
    if (row === null) return [];
    const doors = Array.isArray(row["doorsRead"]) ? row["doorsRead"] : [];
    return [
      {
        position: typeof row["position"] === "number" ? row["position"] : at + 1,
        question: texte(row["question"]),
        answer: typeof row["answer"] === "string" ? row["answer"] : null,
        failure: typeof row["failure"] === "string" ? row["failure"] : null,
        state: STATES.find((one) => one === row["state"]) ?? null,
        doorsRead: doors.flatMap((other) => {
          const door = asRecord(other);
          return door === null
            ? []
            : [
                {
                  tool: texte(door["tool"]),
                  door: texte(door["door"]),
                  status: typeof door["status"] === "number" ? door["status"] : 0,
                },
              ];
        }),
        askedAt: texte(row["askedAt"]),
        answeredAt: typeof row["answeredAt"] === "string" ? row["answeredAt"] : null,
      },
    ];
  });
}

/**
 * `F-2` (R-11): **the sources an answer rests on are named in the banker's words**, by the tool that read them , never by the
 * route's method and path. A tool this closed list does not know is not named at all, rather than shown raw.
 */
const DOOR_WORDS = new Map<string, Parameters<typeof t>[0]>([
  ["lire_le_dossier", "bank.bay.porteDossier"],
  ["lire_l_analyse", "bank.bay.porteAnalyse"],
  ["lire_l_offre", "bank.bay.porteOffre"],
]);

export const doorWords = (doors: readonly QuestionDoorView[]): string =>
  [...new Set(doors.flatMap((door) => DOOR_WORDS.get(door.tool) ?? []))].map((key) => t(key)).join(" · ");

/**
 * WF-BNK-18's suggestions, in the drawing's words , the catalogue's, as 06 §6.5.1 requires. **One, not three** (`F-2`, D-1020-7, truth of capability): « Comparer
 * aux dossiers similaires » asks for precedents, a counter that does not exist yet, and « Simuler sur 60 mois » asks for a
 * calculation the question door cannot make (it opens three read doors and invents no figure), so the bay offers neither.
 */
export const SUGGESTION_KEYS = ["bank.dossier.questionSuggestion1"] as const;

export const SUGGESTIONS: readonly string[] = SUGGESTION_KEYS.map((key) => t(key));

/**
 * `ASK-ONE-PATTERN` (D2 frames 172 to 180, design/bank-v2 5824540f): **the count line's rule, in one place.** « N échanges dans la
 * conversation » counts the STORED, DATED exchanges: an answered one, and one withheld as unverified (frame 160). NOT a refused question
 * (frame 158), a failed one (159), or one still in flight (157: no answer yet, no failure). An exchange with no date is not stored.
 */
export function exchangesCounted(thread: readonly QuestionExchangeView[]): readonly QuestionExchangeView[] {
  return thread.filter((exchange) => {
    if (exchange.askedAt === "") return false;
    if (exchange.state === "unverified") return true;
    if (exchange.state === "refused" || exchange.state === "failed") return false;
    return exchange.failure === null && exchange.answer !== null;
  });
}

/**
 * `ASK-ONE-PATTERN` (frames 173 and 174): **the bay ends in ONE button** that opens the conversation (`aria-haspopup="dialog"`,
 * `aria-expanded`); under it « N échange(s) dans la conversation » when there is a history, and the sentence that the words are a
 * proposition. The small question box is gone: the field, the send and the chips live in the conversation.
 */
export function AskEntry(props: {
  readonly count: number;
  readonly open: boolean;
  readonly onOpen: () => void;
}): ReactElement {
  return (
    <div data-bay="ask" data-testid="bank-question-panneau">
      <button
        type="button"
        data-bay="interroger"
        aria-haspopup="dialog"
        aria-expanded={props.open ? "true" : "false"}
        onClick={props.onOpen}
        data-testid="bank-question-interroger"
      >
        <Maximize2 width={18} height={18} aria-hidden="true" />
        {t("bank.conversation.interroger")}
      </button>
      {props.count === 0 ? null : (
        <p data-bay="ask-count" data-testid="bank-question-compte">
          {props.count === 1
            ? t("bank.conversation.echangeUn")
            : fill("bank.conversation.echangesN", { n: String(props.count) })}
        </p>
      )}
      <p data-bay="ask-note">{t("bank.dossier.questionDecide")}</p>
    </div>
  );
}
